/**
 * Record-centric filter builder — like FilterBar but each filter is tagged with
 * the form it targets (record-view filters carry formId). Filterable fields come
 * from the forms the user has already added columns from (the loaded catalogs).
 */
import { useState } from 'react'
import { nanoid } from 'nanoid'
import { Filter, X, Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover'
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select'
import { isServerQueryable, type FormCatalog, type CatalogField } from '../lib/column-catalog'
import { OP_LABELS, opsForType, NO_VALUE, displayFilterValue } from './filter-bar'
import type { FilterOp, ViewFilter } from '../types/data-view-spec'

interface RecordFilterBarProps {
  catalogs: Record<string, FormCatalog>
  titles: Record<string, string>
  filters: ViewFilter[]
  onChange: (f: ViewFilter[]) => void
}

interface FieldRef { formId: string; field: CatalogField; label: string }

export function RecordFilterBar({ catalogs, titles, filters, onChange }: RecordFilterBarProps) {
  const fieldRefs: FieldRef[] = Object.entries(catalogs).flatMap(([formId, cat]) =>
    cat.fields.filter(isServerQueryable).map((field) => ({
      formId, field, label: `${titles[formId] ?? ''}: ${field.label}`,
    })),
  )
  const remove = (id: string) => onChange(filters.filter((f) => f.id !== id))

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {filters.map((f) => {
        const cat = f.formId ? catalogs[f.formId] : undefined
        const field = cat?.byKey[f.key]
        return (
          <span key={f.id} className="inline-flex items-center gap-1.5 ps-2.5 pe-1 py-1 rounded-full bg-muted text-xs">
            <span className="font-medium">{titles[f.formId ?? ''] ? `${titles[f.formId!]}: ` : ''}{field?.label ?? f.key}</span>
            <span className="text-muted-foreground">{OP_LABELS[f.op]}</span>
            {!NO_VALUE.includes(f.op) && <span>{displayFilterValue(field, f.value)}</span>}
            <button onClick={() => remove(f.id)} className="text-muted-foreground hover:text-foreground"><X className="w-3 h-3" /></button>
          </span>
        )
      })}
      <AddRecordFilter fieldRefs={fieldRefs} onAdd={(f) => onChange([...filters, f])} />
    </div>
  )
}

function AddRecordFilter({ fieldRefs, onAdd }: { fieldRefs: FieldRef[]; onAdd: (f: ViewFilter) => void }) {
  const [open, setOpen] = useState(false)
  const [refKey, setRefKey] = useState('') // `${formId}::${fieldKey}`
  const [op, setOp] = useState<FilterOp>('contains')
  const [value, setValue] = useState('')

  const ref = fieldRefs.find((r) => `${r.formId}::${r.field.fieldKey}` === refKey)
  const ops = ref ? opsForType(ref.field.fieldType) : (['contains'] as FilterOp[])
  const needsValue = !NO_VALUE.includes(op)
  const reset = () => { setRefKey(''); setOp('contains'); setValue('') }

  const submit = () => {
    if (!ref) return
    onAdd({ id: nanoid(6), formId: ref.formId, key: ref.field.fieldKey, op, value: needsValue ? value : undefined })
    reset(); setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset() }}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="gap-1.5 h-7"><Filter className="w-3.5 h-3.5" />افزودن فیلتر</Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 space-y-2">
        <Select value={refKey} onValueChange={(v) => { setRefKey(v); const r = fieldRefs.find((x) => `${x.formId}::${x.field.fieldKey}` === v); setOp(r ? opsForType(r.field.fieldType)[0] : 'contains') }}>
          <SelectTrigger className="h-8"><SelectValue placeholder="انتخاب فیلد" /></SelectTrigger>
          <SelectContent>
            {fieldRefs.map((r) => <SelectItem key={`${r.formId}::${r.field.fieldKey}`} value={`${r.formId}::${r.field.fieldKey}`}>{r.label}</SelectItem>)}
          </SelectContent>
        </Select>
        {ref && (
          <Select value={op} onValueChange={(v) => setOp(v as FilterOp)}>
            <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
            <SelectContent>{ops.map((o) => <SelectItem key={o} value={o}>{OP_LABELS[o]}</SelectItem>)}</SelectContent>
          </Select>
        )}
        {ref && needsValue && (
          ref.field.options ? (
            <Select value={value} onValueChange={setValue}>
              <SelectTrigger className="h-8"><SelectValue placeholder="گزینه" /></SelectTrigger>
              <SelectContent>{Object.entries(ref.field.options).map(([k, lbl]) => <SelectItem key={k} value={k}>{lbl}</SelectItem>)}</SelectContent>
            </Select>
          ) : ref.field.fieldType === 'boolean' ? (
            <Select value={value} onValueChange={setValue}>
              <SelectTrigger className="h-8"><SelectValue placeholder="مقدار" /></SelectTrigger>
              <SelectContent><SelectItem value="true">بله</SelectItem><SelectItem value="false">خیر</SelectItem></SelectContent>
            </Select>
          ) : (
            <Input className="h-8" type={ref.field.fieldType === 'number' ? 'number' : 'text'} placeholder="مقدار"
              value={value} onChange={(e) => setValue(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && submit()} />
          )
        )}
        <Button size="sm" className="w-full gap-1.5" disabled={!ref || (needsValue && !value)} onClick={submit}>
          <Plus className="w-3.5 h-3.5" />اعمال
        </Button>
      </PopoverContent>
    </Popover>
  )
}
